import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Center, Loader, Stack, Text } from '@mantine/core';
import { APP_CONFIG_PATH } from './api.js';

/**
 * Runtime configuration, fetched from the server rather than baked into the bundle, so one
 * built image runs in every environment and switching between stub and OIDC needs no
 * rebuild.
 */

export type AppConfig = {
    authMode: 'stub' | 'oidc';
    cognito: { authority: string; clientId: string } | null;
};

const AppConfigContext = createContext<AppConfig | null>(null);

export const useAppConfig = (): AppConfig => {
    const config = useContext(AppConfigContext);
    if (!config) throw new Error('useAppConfig used outside AppConfigProvider');
    return config;
};

export const AppConfigProvider = ({ children }: { children: ReactNode }) => {
    const [config, setConfig] = useState<AppConfig | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetch(APP_CONFIG_PATH)
            .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
            .then(setConfig)
            .catch((err: Error) => setError(err.message));
    }, []);

    if (error) {
        return (
            <Center h="100vh" p="md">
                <Stack gap="xs" align="center">
                    <Text fw={600}>Cannot reach the server</Text>
                    <Text c="dimmed" ta="center">
                        {error}. The page cannot load its configuration; retry once the hub is reachable.
                    </Text>
                </Stack>
            </Center>
        );
    }

    if (!config) {
        return (
            <Center h="100vh">
                <Loader />
            </Center>
        );
    }

    return <AppConfigContext.Provider value={config}>{children}</AppConfigContext.Provider>;
};
